import React, { useEffect, useRef } from 'react'
import { usePostHogEvent } from '../PostHog/index'
import { OracleCardsSeedQuestion, useOracleCardsSeedQuestions } from './hooks/useOracleCardsSeedQuestions'
import { TarotSeedQuestion, useTarotSeedQuestions } from './hooks/useTarotSeedQuestions'
import { useTarotFeatureState } from './hooks/useTarotFeatureState'
import {
  ACTION_TAROT_NEW_READING,
  ACTION_CLARIFIER_PULL,
  CategorizedTarotSeeds,
  ParsedSeedQuestion,
} from './hooks/seedQuestionTagUtils'
import { randomHex } from './utils/randomHex'
import { makeIcon } from '../OracleCommons/icons'

const TAROT_ICON_ACTIONS = new Set([ACTION_CLARIFIER_PULL, ACTION_TAROT_NEW_READING])

interface ConversationPillButtonsProps {
  chatChannel?: string | null
  chatSessionId?: string
  sendChat: (message: string) => void
  isOperationInProgress?: boolean
  streamCompletionCount?: number
  onCardPull?: () => void
  tarotCategorizedSeeds?: CategorizedTarotSeeds
}

/**
 * Outlined purple pill buttons shown above the input in an active conversation.
 * Supports 'oracle_cards' and 'tarot' channels; additional channels can add
 * their own hook + rendering branch here.
 */
export const ConversationPillButtons: React.FC<ConversationPillButtonsProps> = ({
  chatChannel = null,
  chatSessionId,
  sendChat,
  isOperationInProgress = false,
  streamCompletionCount = 0,
  onCardPull,
  tarotCategorizedSeeds,
}) => {
  const trackMessageSent = usePostHogEvent('ai_message_sent')

  const isOracleCardsSession = chatChannel === 'oracle_cards'
  const isTarotSession = chatChannel === 'tarot'

  const { seedQuestions, refetch } = useOracleCardsSeedQuestions({
    skip: !isOracleCardsSession,
  })

  const { seedQuestions: tarotSeedQuestions, refetch: refetchTarotSeeds } = useTarotSeedQuestions({
    skip: !isTarotSession,
  })

  const {
    isCurrentMonth: isTarotCurrentMonth,
    evictAndRefetch: evictAndRefetchTarotState,
  } = useTarotFeatureState({
    chatSessionId,
    skip: !isTarotSession,
  })

  // Refetch seed questions when a stream completes (e.g., card pull) so the
  // backend returns updated seeds for the new session state
  const prevCompletionCount = useRef(streamCompletionCount)
  useEffect(() => {
    if (streamCompletionCount === prevCompletionCount.current) {
      return
    }
    if (isOracleCardsSession) {
      refetch()
    }
    if (isTarotSession) {
      refetchTarotSeeds()
      evictAndRefetchTarotState()
    }
    prevCompletionCount.current = streamCompletionCount
  }, [
    streamCompletionCount, isOracleCardsSession, isTarotSession,
    refetch, refetchTarotSeeds, evictAndRefetchTarotState,
  ])

  // Oracle Cards: backend returns only the seeds appropriate for the current state
  // Tarot: all seeds shown without filtering
  const activeSeedQuestions = isTarotSession ? tarotSeedQuestions : seedQuestions

  // When categorized seeds are provided, use their length for the emptiness check
  const hasCategorizedSeeds = isTarotSession && tarotCategorizedSeeds
  const categorizedSeedCount = hasCategorizedSeeds
    ? [tarotCategorizedSeeds!.clarify, ...tarotCategorizedSeeds!.others].filter(Boolean).length
    : 0
  const effectiveSeedCount = hasCategorizedSeeds
    ? categorizedSeedCount
    : activeSeedQuestions.length

  if (
    (!isOracleCardsSession && !isTarotSession)
    || isOperationInProgress
    || effectiveSeedCount === 0
    || (isTarotSession && !isTarotCurrentMonth)
  ) {
    return null
  }

  type SeedQuestion = OracleCardsSeedQuestion | TarotSeedQuestion | ParsedSeedQuestion

  const handleClick = (seed: SeedQuestion) => {
    if (seed.tagMeta.action === 'oracle_pull') {
      onCardPull?.()
    }
    if (seed.tagMeta.action) {
      const { action } = seed.tagMeta
      const salt = randomHex(8)
      sendChat(`<Gaia action="${action}" text="${seed.question}" salt="${salt}" />`)
    } else {
      sendChat(seed.question)
    }
    trackMessageSent(seed.question, 'Prompt', 'Prompt')
  }

  const renderPill = (seed: SeedQuestion) => {
    const isPrimary = isTarotSession
      ? seed.tagMeta.action === ACTION_TAROT_NEW_READING
      : !!seed.tagMeta.isPrimary
    const showLeadingIcon = isTarotSession
      && TAROT_ICON_ACTIONS.has(seed.tagMeta.action || '')
    return (
      <button
        key={seed.id}
        type="button"
        className={`oracle-conversation-pills__pill${isPrimary ? ' oracle-conversation-pills__pill--primary' : ''}`}
        onClick={() => handleClick(seed)}
        disabled={isOperationInProgress}
      >
        {showLeadingIcon && (
          <span className="oracle-conversation-pills__pill-icon">
            {makeIcon('plus', '16px')}
          </span>
        )}
        {seed.question}
      </button>
    )
  }

  if (isTarotSession) {
    // When categorized seeds are provided by the parent, use them directly
    // (start is excluded — it's rendered as a FAB elsewhere)
    if (tarotCategorizedSeeds) {
      const categorizedPills: ParsedSeedQuestion[] = [
        tarotCategorizedSeeds.clarify,
        ...tarotCategorizedSeeds.others,
      ].filter(Boolean) as ParsedSeedQuestion[]

      return (
        <div className="oracle-conversation-pills">
          <div className="oracle-conversation-pills__seeds">
            {categorizedPills.map(renderPill)}
          </div>
        </div>
      )
    }

    const startReadingSeed = tarotSeedQuestions
      .find((s) => s.tagMeta.action === ACTION_TAROT_NEW_READING)
    const regularSeeds = tarotSeedQuestions
      .filter((s) => s.tagMeta.action !== ACTION_TAROT_NEW_READING)
      .sort((a, b) => {
        if (a.tagMeta.action === ACTION_CLARIFIER_PULL) return -1
        if (b.tagMeta.action === ACTION_CLARIFIER_PULL) return 1
        return 0
      })

    return (
      <div className="oracle-conversation-pills">
        {regularSeeds.length > 0 && (
          <div className="oracle-conversation-pills__seeds">
            {regularSeeds.map(renderPill)}
          </div>
        )}
        {startReadingSeed && (
          <div className="oracle-conversation-pills__new-reading">
            {renderPill(startReadingSeed)}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="oracle-conversation-pills">
      {activeSeedQuestions.map(renderPill)}
    </div>
  )
}
